'use client'

import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import { PhnxButton } from '@/components/01-atoms/Button/Button'
import { PhnxHubspotform } from '@/components/01-atoms/Hubspotform/Hubspotform'
import { PhnxLogo } from '@/components/01-atoms/Logo/Logo'

import styles from './StickyHeader.module.css'

export interface StickyHeaderProps {
  ctaText?: string
  ctaLink?: {
    story: {
      fullSlug: string
    }
  }
  mobileTitle?: string
  hubspotID?: string
}

export const StickyHeader: React.FC<StickyHeaderProps> = ({
  ctaText,
  ctaLink,
  hubspotID,
  mobileTitle,
}) => {
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null)
  const [showForm, setShowForm] = useState(false)

  // 1. Setup Portal Target and Desktop Padding
  useEffect(() => {
    // Finds the page wrapper to inject the mobile footer into
    const wrapper = document.querySelector('div[data-page-wrapper]') as HTMLElement | null
    setTargetElement(wrapper)

    const topEl = document.querySelector('#top') as HTMLElement | null
    if (!topEl) return

    const media = window.matchMedia('(min-width: 769px)')
    const applyPadding = () => {
      topEl.style.paddingTop = media.matches ? '100px' : ''
    }

    applyPadding()
    media.addEventListener('change', applyPadding)
    return () => {
      media.removeEventListener('change', applyPadding)
      // Reset padding when component unmounts (e.g., navigating away from this page)
      topEl.style.paddingTop = ''
    }
  }, [])

  // 2. Hubspot Form Observer (Shared between Desktop and Mobile)
  useEffect(() => {
    const FORM_DATA_ID = '8ca27351-0fa8-4892-b8a8-6e1d5fa0cf39'
    let observer: IntersectionObserver | null = null
    let attempts = 0

    const findForm = () => {
      const formEl = document.querySelector<HTMLFormElement>(`form[data-form-id="${FORM_DATA_ID}"]`)
      if (!formEl) {
        if (attempts < 20) {
          attempts++
          setTimeout(findForm, 300)
        }
        return
      }

      observer = new IntersectionObserver(
        ([entry]) => {
          setShowForm(entry.intersectionRatio === 0)
        },
        { threshold: 0 }
      )
      observer.observe(formEl)
    }

    findForm()
    return () => observer?.disconnect()
  }, [])

  // 3. Define the Mobile Footer JSX
  const mobileFooter = (
    <>
      {/* Wrap everything in showForm check if hubspotID is present */}
      {(showForm || !hubspotID) && (
        <>
          {hubspotID ? (
            <div className={`${styles.siteStickyMobileCTAButton} ${styles.visible}`}>
              {mobileTitle && <div className={styles.hubspotTitle}>{mobileTitle}</div>}
              <PhnxHubspotform
                title={'Report2026'}
                formId={hubspotID}
                portalId={'441838444'}
                region={'ap1'}
                height={'71px'}
              />
            </div>
          ) : ctaText ? (
            <div className={styles.siteStickyMobileCTAButton}>
              <PhnxButton
                variant="primary"
                size="default"
                type="link"
                href={ctaLink?.story?.fullSlug}
                label={ctaText}
              />
            </div>
          ) : null}
        </>
      )}
    </>
  )

  // 4. Single Return Statement
  return (
    <>
      {/* This renders normally in the document flow (Desktop Header) */}
      <header className={styles.siteStickyHeader}>
        <div className={styles.top}>
          <div className={styles.siteStickyHeaderLogo}>
            <PhnxLogo className={styles.logo} />
          </div>
          <div className={styles.siteStickyHeaderLinks}>
            {hubspotID !== undefined ? (
              <div className={`${styles.hubSpotDesktop} ${showForm ? styles.visible : ''}`}>
                <PhnxHubspotform
                  title={'Report2026'}
                  formId={hubspotID}
                  portalId={'441838444'}
                  region={'ap1'}
                  height={'71px'}
                />
              </div>
            ) : ctaText ? (
              <PhnxButton
                variant="primary"
                size="default"
                type="link"
                href={ctaLink?.story?.fullSlug}
                label={ctaText}
              />
            ) : null}
          </div>
        </div>
      </header>

      {/* This "teleports" the mobile footer to the bottom of the page wrapper */}
      {targetElement && createPortal(mobileFooter, targetElement)}
    </>
  )
}
